import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'

import {first} from '@shared/helpers/array'

import {
    getBankEasyPays,
    selectComplexPayMethod,
    selectEasyAccountPayMethod,
    selectSinglePaymentMethod,
    selectRechargePointPayMethod,
} from '@web/actions/paymentMethod'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {PaymentMethodEntry, PayMethod} from '@web/core/constants'
import {
    selectEasyBankAccount,
    selectRechargeAccount,
    getInitialState,
    AccountState,
    AccountView,
} from '@web/slices/account/slice'

import type {AccountReducers, MakeSliceOptions} from './interfaces'

function hasSelectedAccount({
    accounts,
    selectedAccountKey,
}: {
    accounts: AccountView[]
    selectedAccountKey: AccountView['accountKey']
}): boolean {
    return accounts.some(({accountKey}) => accountKey === selectedAccountKey)
}

export default (options?: MakeSliceOptions) =>
    createSlice<AccountState, AccountReducers, 'account', SliceSelectors<AccountState>>({
        name: 'account',
        initialState: getInitialState(),
        reducers: {},
        extraReducers: (builder) => {
            builder
                .addCase(selectEasyBankAccount, (state, {payload}) => {
                    state.bankEasyPay.selectedAccountKey = payload
                })
                .addCase(selectRechargeAccount, (state, {payload}) => {
                    state.recharge.selectedAccountKey = payload
                })
                .addCase(getBankEasyPays.fulfilled, (state, {payload}) => {
                    const {data, bankEasyPays, recharges, isRegistered = false} = payload
                    state.maintenanceInfo = data.bankEasyPayServiceMaintenanceSchedule
                    state.bankEasyPay.accounts = bankEasyPays
                    state.recharge.accounts = recharges

                    const firstBankEasyPayKey = first(bankEasyPays)?.accountKey ?? ''
                    const firstRechargeKey = first(recharges)?.accountKey ?? ''

                    if (isRegistered) {
                        // 등록한 계좌가 맨 앞으로 정렬된다.
                        state.bankEasyPay.selectedAccountKey = firstBankEasyPayKey
                        state.recharge.selectedAccountKey = firstRechargeKey
                    } else {
                        if (
                            !hasSelectedAccount({
                                accounts: state.bankEasyPay.accounts,
                                selectedAccountKey: state.bankEasyPay.selectedAccountKey ?? '',
                            })
                        ) {
                            state.bankEasyPay.selectedAccountKey = firstBankEasyPayKey
                        }
                        if (
                            !hasSelectedAccount({
                                accounts: state.recharge.accounts,
                                selectedAccountKey: state.recharge.selectedAccountKey ?? '',
                            })
                        ) {
                            state.recharge.selectedAccountKey = firstRechargeKey
                        }
                    }
                })
                .addCase(selectEasyAccountPayMethod.fulfilled, (state, {payload}) => {
                    if (payload.entry === PaymentMethodEntry.INITIALIZE) {
                        state.bankEasyPay.selectedAccountKey = state.bankEasyPay.accounts[0]?.accountKey ?? ''
                    } else {
                        state.bankEasyPay.selectedAccountKey = payload.accountKey ?? null
                    }
                })
                .addCase(selectRechargePointPayMethod.fulfilled, (state, {payload}) => {
                    if (payload.entry === PaymentMethodEntry.INITIALIZE) {
                        if (!state.recharge.selectedAccountKey) {
                            state.recharge.selectedAccountKey =
                                state.recharge.accounts.find(({payable}) => payable)?.accountKey ?? ''
                        }
                    } else {
                        state.recharge.selectedAccountKey = payload.accountKey ?? null
                    }
                })
                .addCase(selectSinglePaymentMethod.fulfilled, (state, {payload}) => {
                    if (payload.selectedPayMethod === PayMethod.BANK_EASY_PAY) {
                        state.bankEasyPay.selectedAccountKey = payload.accountKey ?? null
                    } else if (payload.selectedPayMethod === PayMethod.RECHARGE_POINT) {
                        state.recharge.selectedAccountKey = payload.accountKey ?? null
                    }
                })
            options?.extraReducers?.(builder)
            builder
                .addMatcher(isAnyOf(selectComplexPayMethod.fulfilled), (state, {payload}) => {
                    // FIXME selectedPayMethod 에 RECHARGE_POINT 가 올수 없음? 수정필요
                    if (payload.selectedPayMethod === PayMethod.RECHARGE_POINT) {
                        if (!state.recharge.selectedAccountKey) {
                            state.recharge.selectedAccountKey =
                                state.recharge.accounts.find(({payable}) => payable)?.accountKey ?? ''
                        }
                    } else if (payload.selectedPayMethod === PayMethod.BANK_EASY_PAY) {
                        state.bankEasyPay.selectedAccountKey = state.bankEasyPay.accounts[0]?.accountKey ?? ''
                    }
                })
                .addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                    return payload.account ?? state
                })
        },
    })
